import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { i18n } from './inlineCommentConstants.ts';

const ANNOUNCE_DELAY = 100;

/**
 * Screen-reader-only announcement made when the inline comment sidebar opens.
 *
 * An `aria-live` region must already exist in the DOM before its content changes,
 * otherwise assistive technology (incl. VoiceOver) does not announce it. So the
 * region renders empty and the message is injected after a short delay, producing
 * a content mutation the AT observes. The message is also cleared and re-injected
 * whenever `anchor` changes, so re-anchoring to a different selection re-announces;
 * a full close/open remounts this component and re-announces too.
 *
 * Kept as a small standalone component so its state, timer and effect are
 * self-contained and only run where it is rendered.
 */
export const CommentSidebarAnnouncement: FC<{ anchor?: HTMLElement | null }> = ({ anchor }) => {
	const [showAnnouncement, setShowAnnouncement] = useState(false);

	useEffect(() => {
		// Reset first so re-anchoring produces a genuine empty -> content mutation
		// (rather than leaving stale content that an AT would not re-announce).
		setShowAnnouncement(false);
		const announcementTimer = setTimeout(() => {
			setShowAnnouncement(true);
		}, ANNOUNCE_DELAY);
		return () => {
			clearTimeout(announcementTimer);
		};
	}, [anchor]);

	return (
		<VisuallyHidden testId="comment-sidebar-announcement" role="status">
			{showAnnouncement && <FormattedMessage {...i18n.commentSidebarAnnouncement} />}
		</VisuallyHidden>
	);
};
